import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { forwardRef } from "react";

import { cn } from "@ui/lib/utils";

import { Button as BaseButton } from "../ui/button";

const overrides = cva("", {
  variants: {
    variant: {
      default: "",
      destructive: "",
      outline: "",
      secondary: "",
      ghost: "",
      link: "",
    },
    size: {
      default: "h-10 px-4 py-3",
      sm: "",
      lg: "h-11 rounded-md px-8",
      icon: "h-[33px] w-[40px] p-3",
      xs: "h-8 rounded-[8px] px-3 text-[12px] [&_svg]:h-4 [&_svg]:w-4 space-x-1",
      iconSm: "h-[24px] w-[24px] p-1",
      iconSmCircle: "h-[20px] w-[20px] p-1 rounded-full",
      "2xs": "h-6 rounded-[4px] px-1 bg-red [&_svg]:h-4 [&_svg]:w-4 space-x-1",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof overrides> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ children, loading, ...props }, ref) {
    const content = loading ? (
      props.size === "icon" ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
        </>
      ) : (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {typeof children === "string" ? <span>{children}</span> : children}
        </>
      )
    ) : (
      children
    );

    return (
      <BaseButton
        {...props}
        className={cn(
          {
            "space-x-2": typeof content !== "string" || loading,
          },
          overrides({
            variant: props.variant,
            size: props.size,
            className: props.className,
          }),
        )}
        ref={ref}
      >
        {content}
      </BaseButton>
    );
  },
);

export type ButtonVariant = ButtonProps["variant"];
export type ButtonSize = ButtonProps["size"];
